import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Plus, RefreshCw, Search } from "lucide-react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { tagService } from "@/features/tags/services/tags.service";
import { tagAdminService } from "@/features/tags/services/tags-admin.service";
import type { TagResponseDTO } from "@/features/tags/types";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { applyFieldErrorsToForm } from "@/shared/lib/form-error";
import { toast } from "sonner";
import { TagsTable } from "@/features/tags/components/TagsTable";
import { TagFormDialog } from "@/features/tags/components/TagFormDialog";
import { TagDeleteDialog } from "@/features/tags/components/TagDeleteDialog";
import type { TagFormData } from "@/features/tags/components/TagFormDialog";

const DIALOG_MODE = {
  CREATE: "create",
  EDIT: "edit",
  DELETE: "delete",
} as const;

type DialogMode = (typeof DIALOG_MODE)[keyof typeof DIALOG_MODE];

const tagSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório").max(100),
  description: z.string().max(255).optional(),
  color: z
    .string()
    .regex(
      /^#[0-9A-Fa-f]{6}$/,
      "Cor deve ser um hexadecimal válido (ex: #FF5733)",
    ),
});

export function TagsPage() {
  const { isAdmin } = useAuth();

  const [tags, setTags] = useState<TagResponseDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterName, setFilterName] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null);
  const [dialogContent, setDialogContent] = useState<DialogMode | null>(null);
  const [selectedTag, setSelectedTag] = useState<TagResponseDTO | null>(null);

  const form = useForm<TagFormData>({
    resolver: zodResolver(tagSchema),
    defaultValues: { name: "", description: "", color: "#3B82F6" },
  });

  const loadTags = useCallback(
    async (page: number = 0) => {
      setLoading(true);
      try {
        const service = isAdmin ? tagAdminService : tagService;
        const response = await service.search({
          name: filterName || undefined,
          page,
          size: pageSize,
          sortBy: "name",
          sortDirection: "ASC",
        });

        setTags(response.content);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
        setCurrentPage(page);
      } catch (error: unknown) {
        toast.error(getApiErrorMessage(error, "Erro ao carregar tags"));
      } finally {
        setLoading(false);
      }
    },
    [isAdmin, filterName, pageSize],
  );

  useEffect(() => {
    // Recarrega só ao trocar pageSize; filtros disparam via botão Buscar.
    // loadTags captura filtros via closure — intencional. Padrão legado
    // Família A; migrar para useTagList em refator futuro.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadTags(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  const filteredTags = useMemo(() => {
    if (!searchTerm) return tags;
    const search = searchTerm.toLowerCase();
    return tags.filter(
      (tag) =>
        tag.name.toLowerCase().includes(search) ||
        tag.description?.toLowerCase().includes(search),
    );
  }, [tags, searchTerm]);

  const handleSearch = useCallback(() => {
    loadTags(0);
  }, [loadTags]);

  const handleOpenDialog = useCallback(
    (mode: DialogMode, tag?: TagResponseDTO) => {
      setDialogMode(mode);
      setDialogContent(mode);
      setSelectedTag(tag ?? null);

      if (mode === DIALOG_MODE.EDIT && tag) {
        form.reset({
          name: tag.name,
          description: tag.description ?? "",
          color: tag.color,
        });
      } else if (mode === DIALOG_MODE.CREATE) {
        form.reset({ name: "", description: "", color: "#3B82F6" });
      }
    },
    [form],
  );

  const handleCloseDialog = useCallback(() => {
    setDialogMode(null);
    setTimeout(() => {
      setDialogContent(null);
      setSelectedTag(null);
      form.reset();
    }, 300);
  }, [form]);

  const onSubmit = useCallback(
    async (data: TagFormData) => {
      setLoading(true);
      try {
        const payload = {
          name: data.name,
          description: data.description || undefined,
          color: data.color,
        };

        if (dialogMode === DIALOG_MODE.CREATE) {
          const service = isAdmin ? tagAdminService : tagService;
          const newTag = await service.create(payload);
          toast.success("Tag criada com sucesso!");
          setTags((prev) => [newTag, ...prev]);
          setTotalElements((prev) => prev + 1);
        } else if (dialogMode === DIALOG_MODE.EDIT && selectedTag) {
          const updatedTag = await tagAdminService.update(
            selectedTag.id,
            payload,
          );
          toast.success("Tag atualizada com sucesso!");
          setTags((prev) =>
            prev.map((t) => (t.id === updatedTag.id ? updatedTag : t)),
          );
        }

        handleCloseDialog();
      } catch (error: unknown) {
        if (!applyFieldErrorsToForm(error, form)) {
          toast.error(getApiErrorMessage(error, "Erro ao salvar tag"));
        }
      } finally {
        setLoading(false);
      }
    },
    [dialogMode, selectedTag, isAdmin, handleCloseDialog, form],
  );

  const handleDelete = useCallback(async () => {
    if (!selectedTag) return;

    setLoading(true);
    try {
      await tagAdminService.remove(selectedTag.id);
      toast.success("Tag excluída com sucesso!");

      const updatedTags = tags.filter((t) => t.id !== selectedTag.id);
      setTags(updatedTags);

      const newTotalElements = totalElements - 1;
      setTotalElements(newTotalElements);
      setTotalPages(Math.ceil(newTotalElements / pageSize));

      if (updatedTags.length === 0 && currentPage > 0) {
        await loadTags(currentPage - 1);
      }

      handleCloseDialog();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Erro ao excluir tag"));
    } finally {
      setLoading(false);
    }
  }, [
    selectedTag,
    tags,
    totalElements,
    pageSize,
    currentPage,
    handleCloseDialog,
    loadTags,
  ]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        loadTags(newPage);
      }
    },
    [totalPages, loadTags],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Tags</h1>
          <p className="text-muted-foreground">Gerencie as tags do sistema</p>
        </div>
        <Can permission={ACTIONS.tags.create}>
          <Button
            className="gap-2"
            onClick={() => handleOpenDialog(DIALOG_MODE.CREATE)}
            disabled={loading}
          >
            <Plus className="w-4 h-4" />
            Nova Tag
          </Button>
        </Can>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros de Busca</CardTitle>
          <CardDescription>Filtre as tags por nome</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 flex-col md:flex-row">
            <div className="space-y-2 flex-1">
              <label className="text-sm font-medium">Nome</label>
              <Input
                placeholder="Buscar por nome..."
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button onClick={handleSearch} disabled={loading}>
              <Search className="w-4 h-4 mr-2" />
              Buscar
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setFilterName("");
              }}
            >
              Limpar
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <CardTitle>Lista de Tags</CardTitle>
              <CardDescription>
                {loading
                  ? "Carregando..."
                  : `Mostrando ${filteredTags.length} de ${totalElements} tags`}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Filtrar na tabela..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="md:max-w-xs"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => loadTags(currentPage)}
                disabled={loading}
                title="Atualizar lista"
              >
                <RefreshCw
                  className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                />
              </Button>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="h-10 w-full cursor-pointer rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={loading}
              >
                <option value={10}>10 / página</option>
                <option value={25}>25 / página</option>
                <option value={50}>50 / página</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && tags.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Carregando tags...
            </div>
          ) : filteredTags.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhuma tag encontrada
            </div>
          ) : (
            <TagsTable
              tags={filteredTags}
              loading={loading}
              currentPage={currentPage}
              totalPages={totalPages}
              onEdit={(tag) => handleOpenDialog(DIALOG_MODE.EDIT, tag)}
              onDelete={(tag) => handleOpenDialog(DIALOG_MODE.DELETE, tag)}
              onPageChange={handlePageChange}
            />
          )}
        </CardContent>
      </Card>

      <TagFormDialog
        open={
          dialogMode === DIALOG_MODE.CREATE || dialogMode === DIALOG_MODE.EDIT
        }
        mode={dialogContent === DIALOG_MODE.CREATE ? "create" : "edit"}
        loading={loading}
        form={form}
        onClose={handleCloseDialog}
        onSubmit={onSubmit}
      />

      <TagDeleteDialog
        open={dialogMode === DIALOG_MODE.DELETE}
        loading={loading}
        tag={selectedTag}
        onClose={handleCloseDialog}
        onConfirm={handleDelete}
      />
    </div>
  );
}
